import { useCallback } from 'react';
import { AlignedData, Options } from 'uplot';
import Chart from '../../../graph/LineGraph';
import { TooltipData } from '../../../graph/types';
import { YoutubeStatsMultiTooltip } from '../tooltips/YoutubeStatsMultiTooltip';
import { YoutubeStatsSingleTooltip } from '../tooltips/YoutubeStatsSingleTooltip';
import { ViewersStatsForChart } from '../utils/transform-viewers-stats-for-uplot';
import {
  buildYoutubeWatchUrlAtOffset,
  canShowYoutubeWatchActionForVideo,
  computeVideoOffsetSeconds,
  findVideoForChannel,
} from '../utils/youtube-stats-video-utils';

type YoutubeStatsLineChartProps = {
  options: Options;
  data: AlignedData;
  stats: ViewersStatsForChart | undefined;
  seriesChannelIds: string[];
  timezone: string;
};

/**
 * YouTube stats v2 chart with single-line tooltip text and click-through to YouTube at the hovered offset.
 */
export function YoutubeStatsLineChart(props: YoutubeStatsLineChartProps) {
  const { options, data, stats, seriesChannelIds, timezone } = props;
  const channelMetaById = stats?.metadata?.channels;

  /**
   * Resolves the hovered channel id from the current single-series tooltip payload.
   */
  const resolveHoveredChannelId = useCallback(
    (tooltipData: TooltipData) => {
      if (tooltipData.mode !== 'single') {
        return null;
      }

      const uPlotSeriesIndex = tooltipData.series[0]?.uPlotSeriesIndex ?? tooltipData.seriesIndex;
      if (uPlotSeriesIndex == null) {
        return null;
      }

      return seriesChannelIds[uPlotSeriesIndex - 1] ?? null;
    },
    [seriesChannelIds],
  );

  const resolveYoutubeContext = useCallback(
    (tooltipData: TooltipData) => {
      if (tooltipData.mode !== 'single' || tooltipData.timestampUnix == null) {
        return null;
      }

      const channelId = resolveHoveredChannelId(tooltipData);
      if (!channelId) {
        return null;
      }

      const video = findVideoForChannel(
        stats?.metadata?.videos,
        channelId,
        tooltipData.timestampUnix,
        timezone,
      );
      if (!video?.startedAt || !canShowYoutubeWatchActionForVideo(video, timezone)) {
        return null;
      }

      const offsetSeconds = computeVideoOffsetSeconds(tooltipData.timestampUnix, video, timezone);

      return {
        video,
        videoTitle: video.title,
        offsetSeconds,
      };
    },
    [resolveHoveredChannelId, stats?.metadata?.videos, timezone],
  );

  const renderSingleTooltip = useCallback(
    (tooltipData: TooltipData) => {
      const hoveredChannelId = resolveHoveredChannelId(tooltipData);
      const channelThumbnailUrl = hoveredChannelId
        ? stats?.metadata?.channels?.[hoveredChannelId]?.thumbnailUrl
        : undefined;
      const youtubeContext = resolveYoutubeContext(tooltipData);
      return (
        <YoutubeStatsSingleTooltip
          data={tooltipData}
          channelThumbnailUrl={channelThumbnailUrl}
          videoTitle={youtubeContext?.videoTitle}
          showYoutubeAction={Boolean(youtubeContext)}
        />
      );
    },
    [resolveHoveredChannelId, resolveYoutubeContext, stats?.metadata?.channels],
  );

  const renderMultiTooltip = useCallback(
    (tooltipData: TooltipData) => {
      return (
        <YoutubeStatsMultiTooltip
          data={tooltipData}
          seriesChannelIds={seriesChannelIds}
          channelMetaById={channelMetaById ?? {}}
        />
      );
    },
    [channelMetaById, seriesChannelIds],
  );

  const onPointClick = useCallback(
    (tooltipData: TooltipData) => {
      const youtubeContext = resolveYoutubeContext(tooltipData);
      if (!youtubeContext) {
        return;
      }

      const url = buildYoutubeWatchUrlAtOffset(
        youtubeContext.video.id,
        youtubeContext.offsetSeconds,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [resolveYoutubeContext],
  );

  const pointerOnSingleLineHover = Boolean(
    stats?.metadata?.videos && Object.keys(stats.metadata.videos).length > 0,
  );

  return (
    <Chart
      options={options}
      data={data}
      width=""
      height=""
      timezone={timezone}
      tooltipTimestampFormat="HH:mm"
      pointerOnSingleLineHover={pointerOnSingleLineHover}
      renderSingleTooltip={renderSingleTooltip}
      renderMultiTooltip={renderMultiTooltip}
      onPointClick={onPointClick}
    />
  );
}
